import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
import config from "../config/config";

import { clearSearchSelectionsHelper } from "../util_functions/helpers";

const SearchWorkersContext = createContext();

export const SearchWorkersProvider = ({ children }) => {
  const defaultSearchSelections = {
    suburbValue: "",
    childDetails: null,
    supportTypeDetails: [],
    careActivityDetails: [],
    genderDetails: null,
  };
  const [searchSelections, setSearchSelections] = useState(
    defaultSearchSelections
  );
  const [isSearchSelectionsReady, setIsSearchSelectionsReady] = useState(false);
  const [isSelectionIdReady, setIsSelectionIdReady] = useState(false);
  const [supportTypes, setSupportTypes] = useState([]);
  const [supportActivities, setSupportActivities] = useState([]);

  const [selectionId, setSelectionId] = useState(() => {
    const match = document.cookie.match(/searchSelectionId=([^;]+)/);
    return match ? match[1] : null;
  });

  const getSessionIdFromCookies = useCallback(() => {
    const match = document.cookie.match(/sessionId=([^;]+)/);
    return match ? match[1] : null;
  }, []);

  const getSelectionIdFromCookies = useCallback(() => {
    const match = document.cookie.match(/searchSelectionId=([^;]+)/);
    const id = match ? match[1] : null;
    return id;
  }, []);

  const ensureUserSession = useCallback(() => {
    const sessionId = getSessionIdFromCookies();
    if (!sessionId) {
      console.warn("No sessionId found. Redirecting to login...");
    }
    return sessionId;
  }, [getSessionIdFromCookies]);

  const fetchSearchSelectionsById = useCallback(async (selectionId) => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/v1/worker/search-selections/${selectionId}`
      );
      setSearchSelections(response.data || defaultSearchSelections);
      setIsSearchSelectionsReady(true);
      return response.data;
    } catch (error) {
      console.error("Error fetching selections by ID:", error);
      setSearchSelections(defaultSearchSelections);
      setIsSearchSelectionsReady(true);
    }
  }, []);

  useEffect(() => {
    const selectionIdFromCookies = getSelectionIdFromCookies();
    setSelectionId(selectionIdFromCookies);

    if (selectionIdFromCookies) {
      fetchSearchSelectionsById(selectionIdFromCookies).catch((error) => {
        console.error("Failed to fetch selections from API:", error);
      });
    }
  }, []);

  const updateSearchSelectionField = useCallback(
    async (field, value) => {
      ensureUserSession();
      console.log(`[updateSearchSelectionField] Updating ${field}:`, value);
      let updatedSelections = { ...searchSelections };
      updatedSelections[field] = value;

      setSearchSelections(updatedSelections);

      if (selectionId) {
        try {
          await axios.patch(
            `${config.API_BASE_URL}/v1/worker/search-selections/${selectionId}`,
            { [field]: updatedSelections[field] }
          );
        } catch (error) {
          console.error(`Error updating field ${field} in Redis:`, error);
        }
      } else {
        console.error("Selection ID is missing; cannot patch the selection.");
      }
    },
    [searchSelections, selectionId]
  );

  const saveSearchSelections = useCallback(
    async (selections, isFirstStep = false) => {
      ensureUserSession();
      try {
        if (isFirstStep || !selectionId) {
          const response = await axios.post(
            `${config.API_BASE_URL}/v1/worker/search-selections`,
            selections
          );
          const newSelectionId = response.data.key;
          setSelectionId(newSelectionId);
          document.cookie = `searchSelectionId=${newSelectionId}; path=/`;
          setIsSelectionIdReady(true);
        } else {
          await axios.patch(
            `${config.API_BASE_URL}/v1/worker/search-selections/${selectionId}`,
            selections
          );
          setIsSelectionIdReady(true);
        }
        setSearchSelections(selections);
        setIsSearchSelectionsReady(true);
      } catch (error) {
        console.error("Error saving selections:", error);
      }
    },
    [selectionId, ensureUserSession]
  );

  const fetchSupportTypes = useCallback(async () => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/v1/support-types`
      );
      setSupportTypes(response.data);
      console.log("Support types fetched:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching support types:", error);
      throw error;
    }
  }, []);

  const fetchSupportTypeById = useCallback(async (id) => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/v1/support-types/${id}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching support type with ID ${id}:`, error);
      throw error;
    }
  }, []);

  const saveSupportType = useCallback(
    async (supportType) => {
      try {
        const response = await axios.post(
          `${config.API_BASE_URL}/v1/support-types`,
          supportType
        );
        await fetchSupportTypes();
        return response.data;
      } catch (error) {
        console.error("Error saving support type:", error);
        throw error;
      }
    },
    [fetchSupportTypes]
  );

  const deleteSupportType = useCallback(
    async (id) => {
      try {
        await axios.delete(`${config.API_BASE_URL}/v1/support-types/${id}`);
        await fetchSupportTypes();
      } catch (error) {
        console.error(`Error deleting support type with ID ${id}:`, error);
        throw error;
      }
    },
    [fetchSupportTypes]
  );

  const fetchSupportActivities = useCallback(async () => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/v1/support-activities`
      );
      setSupportActivities(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching support activities:", error);
      throw error;
    }
  }, []);

  const fetchActivitiesBySupportTypeId = useCallback(async (supportTypeId) => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/v1/support-activities/support-type/${supportTypeId}`
      );
      setSupportActivities(response.data);
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching activities for support type ${supportTypeId}:`,
        error
      );
      throw error;
    }
  }, []);

  const fetchActivitiesBySupportTypeLabel = useCallback(
    async (supportTypeLabel) => {
      try {
        const response = await axios.get(
          `${
            config.API_BASE_URL
          }/v1/support-activities/support-type-label/${encodeURIComponent(
            supportTypeLabel
          )}`
        );
        console.log(
          `Activities for support type label "${supportTypeLabel}":`,
          response.data
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error fetching activities for support type label "${supportTypeLabel}":`,
          error
        );
        throw error;
      }
    },
    []
  );

  const fetchSupportActivityById = async (id) => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/v1/support-activities/${id}`
      );
      console.log(`Support activity fetched with ID ${id}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching support activity with ID ${id}:`, error);
      throw error;
    }
  };

  const saveSupportActivity = useCallback(
    async (activity) => {
      try {
        const response = await axios.post(
          `${config.API_BASE_URL}/v1/support-activities`,
          activity
        );
        await fetchSupportActivities();
        return response.data;
      } catch (error) {
        console.error("Error saving support activity:", error);
        throw error;
      }
    },
    [fetchSupportActivities]
  );

  const deleteSupportActivity = useCallback(
    async (id) => {
      try {
        await axios.delete(
          `${config.API_BASE_URL}/v1/support-activities/${id}`
        );
        await fetchSupportActivities();
      } catch (error) {
        console.error(`Error deleting support activity with ID ${id}:`, error);
        throw error;
      }
    },
    [fetchSupportActivities]
  );

  const clearSearchSelections = useCallback(async () => {
    ensureUserSession();
    console.log(`Clearing search selections. Current ID: ${selectionId}`);

    setSearchSelections(defaultSearchSelections);
    setIsSearchSelectionsReady(false);

    if (selectionId) {
      try {
        await clearSearchSelectionsHelper(selectionId);
      } catch (error) {
        console.error(
          `Failed to clear search selections for ID: ${selectionId}`,
          error
        );
      }
    } else {
      console.warn("No selection ID found in cookies or state.");
    }

    setIsSearchSelectionsReady(true);
  }, [selectionId, clearSearchSelectionsHelper]);

  return (
    <SearchWorkersContext.Provider
      value={{
        isSelectionIdReady,
        getSelectionIdFromCookies,
        searchSelections,
        saveSearchSelections,
        updateSearchSelectionField,
        clearSearchSelections,
        supportTypes,
        fetchSupportTypes,
        fetchSupportTypeById,
        saveSupportType,
        deleteSupportType,
        supportActivities,
        fetchSupportActivities,
        fetchActivitiesBySupportTypeId,
        fetchActivitiesBySupportTypeLabel,
        fetchSupportActivityById,
        saveSupportActivity,
        deleteSupportActivity,
      }}
    >
      {children}
    </SearchWorkersContext.Provider>
  );
};

export const useSearchWorkers = () => useContext(SearchWorkersContext);
